import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  API, authHeaders,
  TH, TD, AdminCard, PageHeading,
  Modal, ModalHeader, inputStyle,
} from "./AdminUtils";

const EMPTY_FORM = {
  name: "", description: "", price: "", stock: "", sku: "", imageUrl: "", videoUrl: "",
  features: [],
  specifications: {},
  images: [],
};

const labelStyle = {
  color: "var(--text-muted)",
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  display: "block",
  marginBottom: 5,
};

const chipBtn = (extra = {}) => ({
  background: "var(--bg-secondary)",
  border: "1px solid var(--border-default)",
  color: "var(--text-secondary)",
  padding: "4px 10px",
  borderRadius: "var(--radius-sm)",
  fontSize: 11,
  letterSpacing: "0.04em",
  cursor: "pointer",
  ...extra,
});

const dangerBtn = {
  background: "var(--danger-bg)",
  border: "1px solid var(--danger-border)",
  color: "var(--danger-text)",
  padding: "4px 10px",
  borderRadius: "var(--radius-sm)",
  fontSize: 11,
  cursor: "pointer",
};

const changed = (a, b) => JSON.stringify(a) !== JSON.stringify(b);

// ─── R2 helpers ───────────────────────────────────────────────────────────────
async function uploadToR2(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await axios.post(`${API}/admin/upload/image`, fd, {
    headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
  });
  return res.data.url;
}

async function uploadVideoToR2(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await axios.post(`${API}/admin/upload/video`, fd, {
    headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
  });
  return res.data.url;
}

async function deleteFromR2(url) {
  if (!url) return;
  try {
    await axios.delete(`${API}/admin/upload`, {
      params: { url },
      headers: authHeaders(),
    });
  } catch {}
}

// ─── UploadButton ─────────────────────────────────────────────────────────────
function UploadButton({ onUploaded, label = "Upload Image", style = {} }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState("");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const url = await uploadToR2(file);
      onUploaded(url);
    } catch {
      setErr("Upload failed.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div style={style}>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
      <button
        type="button"
        onClick={() => inputRef.current.click()}
        disabled={busy}
        style={{
          ...chipBtn({ borderStyle: "dashed", color: busy ? "var(--text-muted)" : "var(--accent)", borderColor: "var(--accent)" }),
          display: "inline-flex", alignItems: "center", gap: 6,
        }}
      >
        {busy ? <><span className="tc-spinner" style={{ width: 11, height: 11, borderWidth: 1.5 }} />Uploading…</> : <>↑ {label}</>}
      </button>
      {err && <span style={{ fontSize: 11, color: "var(--danger-text)", marginLeft: 8 }}>{err}</span>}
    </div>
  );
}

// ─── ImageUrlField — upload + URL + clear ─────────────────────────────────────
function ImageUrlField({ label = "Main Image", value, onChange }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {value ? (
          <div style={{ position: "relative", flexShrink: 0 }}>
            <img
              src={value}
              alt=""
              style={{ width: 44, height: 44, objectFit: "cover", borderRadius: "var(--radius-sm)", border: "1px solid var(--border-subtle)", display: "block" }}
            />
            <button
              type="button"
              onClick={() => onChange("")}
              title="Remove image"
              style={{ position: "absolute", top: -6, right: -6, width: 16, height: 16, borderRadius: "50%", background: "var(--danger-text)", border: "none", color: "#fff", fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
            >×</button>
          </div>
        ) : null}
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste URL or upload →"
          style={{ ...inputStyle, flex: 1, fontSize: 12 }}
        />
        <UploadButton label="Upload" onUploaded={onChange} style={{ flexShrink: 0 }} />
      </div>
    </div>
  );
}

// ─── VideoUploadField ─────────────────────────────────────────────────────────
function VideoUploadField({ label = "Product Video", value, onChange }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState("");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const url = await uploadVideoToR2(file);
      onChange(url);
    } catch {
      setErr("Upload failed.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <label style={labelStyle}>{label}</label>
      {value && (
        <video
          src={value}
          controls
          style={{ width: "100%", maxHeight: 140, borderRadius: "var(--radius-sm)", border: "1px solid var(--border-subtle)", marginBottom: 8, background: "#000", display: "block" }}
        />
      )}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste video URL or upload →"
          style={{ ...inputStyle, flex: 1, fontSize: 12 }}
        />
        <input ref={inputRef} type="file" accept="video/mp4,video/webm,video/quicktime" style={{ display: "none" }} onChange={handleFile} />
        <button
          type="button"
          onClick={() => inputRef.current.click()}
          disabled={busy}
          style={{
            ...chipBtn({ borderStyle: "dashed", color: busy ? "var(--text-muted)" : "var(--accent)", borderColor: "var(--accent)" }),
            display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0,
          }}
        >
          {busy ? <><span className="tc-spinner" style={{ width: 11, height: 11, borderWidth: 1.5 }} />Uploading…</> : <>↑ Upload</>}
        </button>
        {value && (
          <button type="button" onClick={() => onChange("")} style={{ ...dangerBtn, flexShrink: 0 }} title="Remove video">×</button>
        )}
      </div>
      {err && <span style={{ fontSize: 11, color: "var(--danger-text)", marginTop: 4, display: "block" }}>{err}</span>}
    </div>
  );
}

// ─── FeaturesEditor ───────────────────────────────────────────────────────────
function FeaturesEditor({ features, onChange }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...features, v]);
    setDraft("");
  };
  return (
    <div>
      <label style={labelStyle}>Features</label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
        {features.map((f, i) => (
          <span key={i} style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            background: "var(--bg-secondary)", border: "1px solid var(--border-default)",
            borderRadius: "var(--radius-sm)", padding: "3px 8px", fontSize: 12, color: "var(--text-secondary)",
          }}>
            {f}
            <button onClick={() => onChange(features.filter((_, idx) => idx !== i))} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--danger-text)", fontWeight: 700, lineHeight: 1, padding: 0 }}>×</button>
          </span>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="Add feature…" style={{ ...inputStyle, flex: 1 }} />
        <button onClick={add} style={chipBtn()}>+ Add</button>
      </div>
    </div>
  );
}

// ─── SpecsEditor ──────────────────────────────────────────────────────────────
function SpecsEditor({ specs, onChange }) {
  const [draftKey, setDraftKey] = useState("");
  const [draftVal, setDraftVal] = useState("");
  const add = () => {
    const k = draftKey.trim(), v = draftVal.trim();
    if (!k || !v) return;
    onChange({ ...specs, [k]: v });
    setDraftKey(""); setDraftVal("");
  };
  const remove = (k) => { const s = { ...specs }; delete s[k]; onChange(s); };
  return (
    <div>
      <label style={labelStyle}>Specifications</label>
      {Object.keys(specs).length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 8 }}>
          <tbody>
            {Object.entries(specs).map(([k, v]) => (
              <tr key={k} style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                <td style={{ padding: "5px 8px", fontSize: 12, color: "var(--text-muted)", width: "40%" }}>{k}</td>
                <td style={{ padding: "5px 8px", fontSize: 12, color: "var(--text-primary)" }}>{v}</td>
                <td style={{ padding: "5px 8px", textAlign: "right" }}><button onClick={() => remove(k)} style={dangerBtn}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ display: "flex", gap: 6 }}>
        <input value={draftKey} onChange={(e) => setDraftKey(e.target.value)} placeholder="Key…" style={{ ...inputStyle, flex: 1 }} />
        <input value={draftVal} onChange={(e) => setDraftVal(e.target.value)} placeholder="Value…" style={{ ...inputStyle, flex: 1 }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
        <button onClick={add} style={chipBtn()}>+ Add</button>
      </div>
    </div>
  );
}

// ─── ImagesEditor — gallery images with delete ────────────────────────────────
function ImagesEditor({ images, onChange }) {
  const [draft, setDraft] = useState("");
  const addUrl = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...images, v]);
    setDraft("");
  };
  return (
    <div>
      <label style={labelStyle}>Additional Images</label>
      {images.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
          {images.map((url, i) => (
            <div key={i} style={{ position: "relative" }}>
              <img src={url} alt="" style={{ width: 56, height: 56, objectFit: "cover", borderRadius: "var(--radius-sm)", border: "1px solid var(--border-subtle)" }} />
              <button
                onClick={() => onChange(images.filter((_, idx) => idx !== i))}
                title="Remove image"
                style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", background: "var(--danger-text)", border: "none", color: "#fff", fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
              >×</button>
            </div>
          ))}
        </div>
      )}
      <UploadButton label="Upload Image" onUploaded={(url) => onChange([...images, url])} style={{ marginBottom: 8 }} />
      <div style={{ display: "flex", gap: 8 }}>
        <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(); } }} placeholder="Or paste image URL…" style={{ ...inputStyle, flex: 1, fontSize: 12 }} />
        <button onClick={addUrl} style={chipBtn()}>+ Add</button>
      </div>
    </div>
  );
}

// ─── VideoQuickEditModal ──────────────────────────────────────────────────────
function VideoQuickEditModal({ product, onClose, onSaved }) {
  const [url, setUrl]             = useState(product.videoUrl ?? "");
  const [saving, setSaving]       = useState(false);
  const [err, setErr]             = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const inputRef = useRef();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setUploadErr("");
    try {
      setUrl(await uploadVideoToR2(file));
    } catch {
      setUploadErr("Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const apply = async () => {
    setSaving(true); setErr("");
    try {
      await axios.patch(`${API}/admin/products/${product.id}/video`, null, { params: { videoUrl: url }, headers: authHeaders() });
      if (product.videoUrl && product.videoUrl !== url) deleteFromR2(product.videoUrl);
      onSaved(); onClose();
    } catch (e) {
      setErr(e.response?.data?.message || "Failed to update video.");
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} width={460}>
      <ModalHeader title="Product Video" onClose={onClose} />
      {url && (
        <video src={url} controls style={{ width: "100%", maxHeight: 220, borderRadius: "var(--radius-sm)", border: "1px solid var(--border-subtle)", background: "#000", display: "block", marginBottom: 16 }} />
      )}
      <input ref={inputRef} type="file" accept="video/mp4,video/webm,video/quicktime" style={{ display: "none" }} onChange={handleFile} />
      <button
        type="button"
        onClick={() => inputRef.current.click()}
        disabled={uploading}
        style={{ ...chipBtn({ borderStyle: "dashed", color: uploading ? "var(--text-muted)" : "var(--accent)", borderColor: "var(--accent)" }), display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%", marginBottom: 12 }}
      >
        {uploading ? <><span className="tc-spinner" style={{ width: 11, height: 11, borderWidth: 1.5 }} />Uploading…</> : <>↑ Upload from device (mp4 / webm / mov, max 100 MB)</>}
      </button>
      {uploadErr && <div style={{ fontSize: 11, color: "var(--danger-text)", marginBottom: 8 }}>{uploadErr}</div>}
      <label style={labelStyle}>Or enter URL</label>
      <input style={inputStyle} value={url} placeholder="https://…" onChange={(e) => setUrl(e.target.value)} autoFocus={!url} />
      {url && (
        <button type="button" onClick={() => setUrl("")} style={{ ...dangerBtn, display: "block", width: "100%", textAlign: "center", marginTop: 8 }}>
          Remove Video
        </button>
      )}
      {err && (
        <div style={{ marginTop: 10, fontSize: 12, color: "var(--danger-text)", padding: "8px 12px", background: "var(--danger-bg)", border: "1px solid var(--danger-border)", borderRadius: "var(--radius-sm)" }}>{err}</div>
      )}
      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
        <button onClick={apply} disabled={saving} className="tc-btn tc-btn-primary" style={{ flex: 1, justifyContent: "center" }}>{saving ? "Saving…" : "Save Video"}</button>
        <button onClick={onClose} className="tc-btn tc-btn-secondary">Cancel</button>
      </div>
    </Modal>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AdminProducts() {
  const [products,  setProducts]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [origForm,  setOrigForm]  = useState(EMPTY_FORM);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState("");
  const [search,    setSearch]    = useState("");
  const [qe,        setQe]        = useState(null);   // quick-edit price/sku/stock
  const [videoQe,   setVideoQe]   = useState(null);   // video quick-edit modal

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/products`, { headers: authHeaders() })
      .then((r) => setProducts(Array.isArray(r.data) ? r.data : []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setOrigForm(EMPTY_FORM);
    setEditingId(null);
    setError("");
    setShowForm(true);
  };

  const openEdit = (p) => {
    const base = {
      name:           p.name        ?? "",
      description:    p.description ?? "",
      price:          p.price       ?? "",
      stock:          p.stock       ?? "",
      sku:            p.sku         ?? "",
      imageUrl:       p.imageUrl    ?? "",
      videoUrl:       p.videoUrl    ?? "",
      features:       [],
      specifications: {},
      images:         [],
    };
    setForm(base);
    setOrigForm(base);
    setEditingId(p.id);
    setError("");

    axios.get(`${API}/admin/products/${p.id}/details`, { headers: authHeaders() })
      .then((r) => {
        const rawImages = r.data.images ?? [];
        const imageUrls = rawImages.map(img => (typeof img === "object" ? (img.url ?? img.imageUrl) : img));
        const patch = {
          features:       r.data.features       ?? [],
          specifications: r.data.specifications ?? {},
          images:         imageUrls,
        };
        setForm((prev)     => ({ ...prev, ...patch }));
        setOrigForm((prev) => ({ ...prev, ...patch }));
      })
      .catch(() => {});

    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.price) { setError("Name and price are required."); return; }
    setSaving(true);
    setError("");
    try {
      let savedId = editingId;
      if (editingId) {
        await axios.put(`${API}/admin/products/${editingId}`, form, { headers: authHeaders() });
        if (origForm.imageUrl && origForm.imageUrl !== form.imageUrl) deleteFromR2(origForm.imageUrl);
        if (origForm.videoUrl && origForm.videoUrl !== form.videoUrl) deleteFromR2(origForm.videoUrl);
      } else {
        const r = await axios.post(`${API}/admin/products`, form, { headers: authHeaders() });
        savedId = r.data?.id;
      }

      if (savedId) {
        const calls = [];
        if (changed(form.features, origForm.features))
          calls.push(axios.post(`${API}/admin/products/${savedId}/features`, { features: form.features }, { headers: authHeaders() }));
        if (changed(form.specifications, origForm.specifications))
          calls.push(axios.post(`${API}/admin/products/${savedId}/specifications`, { specifications: form.specifications }, { headers: authHeaders() }));
        if (changed(form.images, origForm.images)) {
          const removedUrls = origForm.images.filter(url => !form.images.includes(url));
          calls.push(axios.post(`${API}/admin/products/${savedId}/images`, { images: form.images }, { headers: authHeaders() }));
          removedUrls.forEach(url => deleteFromR2(url));
        }
        if (calls.length > 0) await Promise.allSettled(calls);
      }

      setShowForm(false);
      load();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to save product.");
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (product) => {
    let additionalImageUrls = [];
    try {
      const r = await axios.get(`${API}/admin/products/${product.id}/details`, { headers: authHeaders() });
      additionalImageUrls = (r.data.images ?? [])
        .map(img => typeof img === "object" ? (img.url ?? img.imageUrl) : img)
        .filter(Boolean);
    } catch {}

    await axios.delete(`${API}/admin/products/${product.id}`, { headers: authHeaders() });

    deleteFromR2(product.imageUrl);
    deleteFromR2(product.videoUrl);
    additionalImageUrls.forEach(url => deleteFromR2(url));

    setProducts(prev => prev.filter(p => p.id !== product.id));
  };


  const applyQe = async () => {
    if (!qe) return;
    await axios.patch(`${API}/admin/products/${qe.id}/${qe.field}`, null, { params: { [qe.field]: qe.value }, headers: authHeaders() });
    setQe(null);
    load();
  };

  const filtered = products.filter((p) =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.sku?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <PageHeading
        title="Products"
        sub={`${products.length} items in catalogue`}
        action={
          <button onClick={openCreate} className="tc-btn tc-btn-primary" style={{ fontSize: 13, padding: "9px 20px" }}>
            + Add Product
          </button>
        }
      />

      <div style={{ marginBottom: 20 }}>
        <input
          placeholder="Search by name or SKU…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...inputStyle, maxWidth: 320 }}
        />
      </div>

      {/* ── Create / Edit modal ── */}
      {showForm && (
        <Modal onClose={() => setShowForm(false)} width={560}>
          <ModalHeader title={editingId ? "Edit Product" : "New Product"} onClose={() => setShowForm(false)} />
          <div style={{ display: "flex", flexDirection: "column", gap: 14, maxHeight: "72vh", overflowY: "auto", paddingRight: 4 }}>
            {[
              { label: "Name *",      key: "name" },
              { label: "Description", key: "description" },
              { label: "Price (₹) *", key: "price", type: "number" },
              { label: "Stock",       key: "stock", type: "number" },
              { label: "SKU",         key: "sku" },
            ].map((f) => (
              <div key={f.key}>
                <label style={labelStyle}>{f.label}</label>
                <input
                  type={f.type || "text"}
                  value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  style={inputStyle}
                />
              </div>
            ))}

            <ImageUrlField
              label="Main Image"
              value={form.imageUrl}
              onChange={(url) => setForm({ ...form, imageUrl: url })}
            />

            <VideoUploadField
              label="Product Video (mp4 / webm / mov, max 100 MB)"
              value={form.videoUrl}
              onChange={(url) => setForm({ ...form, videoUrl: url })}
            />

            <div style={{ borderTop: "1px solid var(--border-subtle)", margin: "4px 0" }} />
            <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.1em" }}>Detailed Info</div>

            <FeaturesEditor features={form.features}   onChange={(v) => setForm({ ...form, features: v })}       />
            <SpecsEditor    specs={form.specifications} onChange={(v) => setForm({ ...form, specifications: v })} />
            <ImagesEditor   images={form.images}        onChange={(v) => setForm({ ...form, images: v })}         />

            {error && (
              <div style={{ color: "var(--danger-text)", fontSize: 13, padding: "8px 12px", background: "var(--danger-bg)", borderRadius: "var(--radius-sm)", border: "1px solid var(--danger-border)" }}>
                {error}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button onClick={save} disabled={saving} className="tc-btn tc-btn-primary" style={{ flex: 1, justifyContent: "center" }}>
                {saving ? "Saving…" : editingId ? "Save Changes" : "Create Product"}
              </button>
              <button onClick={() => setShowForm(false)} className="tc-btn tc-btn-secondary">Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Quick-edit modal (price / sku / stock) ── */}
      {qe && (
        <Modal onClose={() => setQe(null)} width={360}>
          <ModalHeader title={`Edit ${qe.field}`} onClose={() => setQe(null)} />
          <label style={labelStyle}>New value</label>
          <input
            style={inputStyle}
            value={qe.value}
            autoFocus
            onChange={(e) => setQe({ ...qe, value: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") applyQe(); }}
          />
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button onClick={applyQe} className="tc-btn tc-btn-primary" style={{ flex: 1, justifyContent: "center" }}>Apply</button>
            <button onClick={() => setQe(null)} className="tc-btn tc-btn-secondary">Cancel</button>
          </div>
        </Modal>
      )}

      {/* ── Video quick-edit modal ── */}
      {videoQe && (
        <VideoQuickEditModal product={videoQe} onClose={() => setVideoQe(null)} onSaved={load} />
      )}

      {/* ── Products table ── */}
      <AdminCard>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <TH>ID</TH><TH>Image</TH><TH>Name</TH><TH>SKU</TH><TH>Price</TH><TH>Stock</TH><TH>Actions</TH>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr
                key={p.id}
                onMouseEnter={(e) => e.currentTarget.style.background = "var(--bg-hover)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                style={{ transition: "background var(--transition)" }}
              >
                <TD style={{ color: "var(--text-muted)", width: 50 }}>#{p.id}</TD>

                <TD style={{ width: 60 }}>
                  <div style={{ position: "relative", display: "inline-block" }}>
                    {p.imageUrl ? (
                      <img
                        src={p.imageUrl}
                        alt=""
                        onClick={() => openEdit(p)}
                        title="Click to edit product"
                        style={{ width: 44, height: 44, borderRadius: "var(--radius-sm)", objectFit: "cover", border: "1px solid var(--border-subtle)", cursor: "pointer", display: "block" }}
                      />
                    ) : (
                      <button
                        onClick={() => openEdit(p)}
                        title="Click to edit product"
                        style={{ width: 44, height: 44, borderRadius: "var(--radius-sm)", background: "var(--bg-secondary)", border: "1px dashed var(--border-default)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)", fontSize: 18, cursor: "pointer" }}
                      >↑</button>
                    )}
                    {p.videoUrl && (
                      <span
                        title="Has video"
                        style={{ position: "absolute", bottom: -4, right: -4, background: "var(--accent)", color: "#000", fontSize: 8, fontWeight: 700, padding: "1px 3px", borderRadius: 2, lineHeight: 1.4, letterSpacing: "0.04em" }}
                      >VID</span>
                    )}
                  </div>
                </TD>

                <TD>
                  <div style={{ fontWeight: 500, color: "var(--text-primary)", fontSize: 14 }}>{p.name}</div>
                  {p.description && (
                    <div style={{ color: "var(--text-muted)", fontSize: 11, marginTop: 2 }}>
                      {p.description.slice(0, 50)}{p.description.length > 50 ? "…" : ""}
                    </div>
                  )}
                </TD>

                <TD>
                  <button onClick={() => setQe({ id: p.id, field: "sku", value: p.sku ?? "" })} style={{ fontFamily: "monospace", fontSize: 12, color: "var(--text-secondary)", textDecoration: "underline dotted", textUnderlineOffset: 3 }} title="Click to edit SKU">
                    {p.sku || "—"}
                  </button>
                </TD>

                <TD>
                  <button onClick={() => setQe({ id: p.id, field: "price", value: p.price ?? "" })} style={{ color: "var(--accent)", fontWeight: 600, fontSize: 14, textDecoration: "underline dotted", textUnderlineOffset: 3 }} title="Click to edit price">
                    ₹{p.price}
                  </button>
                </TD>

                <TD>
                  <button
                    onClick={() => setQe({ id: p.id, field: "stock", value: p.stock ?? "" })}
                    style={{ fontWeight: 600, fontSize: 14, textDecoration: "underline dotted", textUnderlineOffset: 3, color: p.stock > 5 ? "var(--success-text)" : p.stock > 0 ? "var(--warning-text)" : "var(--danger-text)" }}
                    title="Click to edit stock"
                  >
                    {p.stock}
                  </button>
                </TD>

                <TD>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button onClick={() => openEdit(p)} style={chipBtn()}>Edit</button>
                    <button onClick={() => setVideoQe(p)} style={chipBtn({ color: p.videoUrl ? "var(--accent)" : "var(--text-secondary)" })}>Video</button>
                    <button onClick={() => deleteProduct(p)} style={dangerBtn}>Delete</button>
                  </div>
                </TD>
              </tr>
            ))}
          </tbody>
        </table>

        {loading && (
          <div style={{ padding: 40, display: "flex", justifyContent: "center" }}><div className="tc-spinner" /></div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="tc-empty" style={{ padding: 48 }}>
            <div className="tc-empty-dot" />
            {search ? "No products match your search" : "No products yet"}
          </div>
        )}
      </AdminCard>

      <p style={{ color: "var(--text-muted)", fontSize: 11, marginTop: 12 }}>
        Tip: Click <strong style={{ color: "var(--text-secondary)" }}>SKU</strong>, <strong style={{ color: "var(--text-secondary)" }}>Price</strong>, or <strong style={{ color: "var(--text-secondary)" }}>Stock</strong> to quick-edit. Click a product thumbnail to open the edit form. Use <strong style={{ color: "var(--text-secondary)" }}>Video</strong> to attach or replace a product video.
      </p>
    </div>
  );
}
